import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import * as moment from 'moment';
import { find, sortBy, startCase, toLower } from 'lodash';
import * as queryString from 'query-string';
import * as initials from 'initials';
import { Icon } from '../';
import {
  DraggableItem,
  DraggableContainer,
} from '@wuweiweiwu/react-shopify-draggable';
import { setBookingProvisionalVehicle } from '../../redux/actions/bookings';
import { setReservationProvisionalVehicle } from '../../redux/actions/reservations';
import Loader from '../Loader/Loader';

interface Props {
  date: any;
  vehicleTypeFilter: (a) => Array<any>;
  holidays?: Array<any>;
  reservations?: Array<any>;
  bookings?: Array<any>;
  vehicles?: Array<any>;
  vehicleTypes?: Array<any>;
  handleReload: Function;
  dispatch: Function;
}

interface State {
  days: Array<any>;
  isMovingEvent: boolean;
}

const DAY_RANGE = 21;

class WeekBlock extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      days: [],
      isMovingEvent: false,
    };
  }

  /**
   * Determine what events occur on the date specified
   * @param {Array} events
   * @param {Date} date
   * @return {Array}
   */
  getEvents(events, date) {
    const results = [];
    if (events !== undefined) {
      events.forEach((event) => {
        const start = moment(event.start);
        const end = moment(event.end);
        const endDate = date.clone().set({
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 999,
        });
        if (endDate.diff(start) >= 0 && date.diff(end) <= 0) {
          event.duration = 1 + moment(event.end).diff(moment(event.start), 'd');
          results.push(event);
        }
      });
    }
    return results;
  }

  /**
   * Prepare the data of the component to render
   */
  prepareDays() {
    const days = [];
    const date = this.props.date.clone();
    for (let i = 0; i < DAY_RANGE; i++) {
      const bookings = this.getEvents(this.props.bookings, date);
      const reservations = this.getEvents(this.props.reservations, date);
      const holidays = this.getEvents(this.props.holidays, date);
      days.push({ holidays, reservations, bookings, date: date.clone() });
      date.add(1, 'days');
    }
    return days;
  }

  /**
   * Render the topmost dates
   * @param {Array} days
   */
  renderDates(days) {
    return (
      <div className="columns is-marginless is-mobile">
        <div className="column weekblock--key" />
        {days.map((day, index) => (
          <div className="column weekblock--key" key={index}>
            <small>
              <strong className="is-hidden-widescreen">
                {day.date.format('ddd')}
                <br />
                {day.date.format('Do')}
              </strong>
              <strong className="is-hidden-touch is-hidden-desktop-only is-block-widescreen">
                {day.date.format('ddd')}
                <br />
                {day.date.format('Do')}
              </strong>
            </small>
          </div>
        ))}
      </div>
    );
  }

  /**
   * Render a single vehicle key
   * @param {Object} vehicle
   */
  renderVehicle(vehicle) {
    const type = find(this.props.vehicleTypes, { id: vehicle.vehicle_type_id });
    if (type === undefined) {
      return <div key={vehicle.id} className="weekblock--vehicle" />;
    }
    return (
      <div key={vehicle.id} className="weekblock--vehicle">
        <div>
          <span className="avatar image is-32x32">
            <img src={type.image} alt={type.name} />
          </span>
          <small>{type.name}</small>
        </div>
      </div>
    );
  }

  /**
   * Render a single day column
   * @param {Object} day
   * @param {Number} column
   */
  renderDay(day, column) {
    const classes = ['weekblock--day', 'column'];
    if (day.holidays.length > 0) {
      classes.push('has-holiday');
    }
    if (moment().isSame(day.date, 'd')) {
      classes.push('is-current-day');
    }
    let vehicleTypeID: any = false;
    let eventID: any = false;
    let eventType: any = false;
    let eventVehicleID: any = false;
    let newVehicleID: any = false;
    let newVehicleTypeID: any = false;
    return (
      <DraggableContainer
        key={day.date.format('YYYY-MM-DD')}
        className={classes.join(' ')}
        style={{ animationDelay: `${(Math.abs(column - 6.5) - 0.5) / 32}s` }}
        type="swappable"
        // We're only interest in events (bookings and reservations)
        // If the event is a blank spot, cancel the dragging event
        onDragStart={(e) => {
          const element = e.source.firstChild;
          if (element.classList.contains('is-free')) {
            e.cancel();
            return;
          }
          eventID = element.getAttribute('data-id');
          eventType = element.getAttribute('data-type');
          eventVehicleID = element.getAttribute('data-vehicle-id');
          vehicleTypeID = element.getAttribute('data-vehicle-type-id');
        }}
        // Keep track of the potential new vehicle to assign
        // But do not actually "swap"
        onSwappableSwap={(e) => {
          const element = e.data.over.firstChild;
          newVehicleID = element.getAttribute('data-vehicle-id');
          newVehicleTypeID = element.getAttribute('data-vehicle-type-id');
          e.cancel();
        }}
        // Check if the vehicle hovered over is of the same vehicle type
        // If so, update the booking on record
        onDragStop={async (e) => {
          if (
            parseInt(vehicleTypeID, 10) !== parseInt(newVehicleTypeID, 10) ||
            parseInt(eventVehicleID, 10) === parseInt(newVehicleID, 10)
          ) {
            return;
          }
          let response = undefined;
          this.setState({ isMovingEvent: true });
          if (eventType === 'hire') {
            response = await this.props.dispatch(
              setBookingProvisionalVehicle(eventID, newVehicleID),
            );
          } else if (eventType === 'reservation') {
            response = await this.props.dispatch(
              setReservationProvisionalVehicle(eventID, newVehicleID),
            );
          }
          if (!response || response.error) {
            this.setState({ isMovingEvent: false });
            return;
          }
          this.props.handleReload(() => {
            this.setState({ isMovingEvent: false });
          });
        }}
      >
        {sortBy(this.props.vehicles, ['vehicle_type_size', 'vehicle_type_id'])
          .filter(this.props.vehicleTypeFilter)
          .map((vehicle, row) => this.renderEvent(column, row, day, vehicle))}
      </DraggableContainer>
    );
  }

  /**
   * Render a single event block
   * @param {Number} column
   * @param {Number} row
   * @param {Object} day
   * @param {Object} vehicle
   */
  renderEvent(column, row, day, vehicle) {
    const classes = [];
    let type = 'none';
    let tooltip = `Create ${vehicle.vehicle_type_name} Booking`;
    let event: any = {
      id: 0,
      contact_name: 'N/A',
    };

    if (day.reservations.length > 0) {
      day.reservations.forEach((reservation) => {
        if (reservation.provisional_vehicle_id === vehicle.id) {
          type = 'reservation';
          classes.push('is-reservation');
          tooltip = `${reservation.name} (${startCase(
            toLower(reservation.type),
          )})`;
          event = reservation;
        }
      });
    }

    if (type !== 'reservation' && day.bookings.length > 0) {
      day.bookings.forEach((booking) => {
        if (booking.provisional_vehicle.id === vehicle.id) {
          type = 'hire';
          classes.push('is-hire');
          classes.push(`is-hire-${toLower(booking.status)}`);
          tooltip = booking.contact_name || 'N/A';
          if (
            ['UNASSIGNED', 'READY', 'ON-HOLD'].indexOf(booking.status) !== -1
          ) {
            tooltip += ` (${startCase(toLower(booking.status))})`;
          }
          event = booking;
        }
      });
    }

    if (event.id !== 0) {
      tooltip += ` • ${moment(event.start).format('Do MMM')} - ${moment(
        event.end,
      ).format('Do MMM')} (${event.duration} day${
        event.duration !== 1 ? 's' : ''
      })`;
      if (moment(event.start).isSame(day.date, 'd')) {
        classes.push('is-start-date');
      }
      if (moment(event.end).isSame(day.date, 'd')) {
        classes.push('is-end-date');
      }
      if (event.vehicle) {
        tooltip += ` • ${event.vehicle.registration}`;
      }
    }

    if (type === 'none') {
      classes.push('is-free');
    } else {
      classes.push('is-not-free');
    }

    if (tooltip !== '') {
      classes.push('tooltip is-tooltip-top is-tooltip-multiline');
    }

    // Return the icon name for the reservation event
    const reservationIcon = (event) => {
      switch (event.type) {
        case 'MAINTENANCE':
          return 'cogs';
        case 'BROKEN':
          return 'chain-broken';
      }
      return 'lock';
    };

    const result = (
      <div
        className={`weekblock--event ${classes.join(' ')}`}
        data-tooltip={tooltip}
        data-id={event.id}
        data-type={type}
        data-vehicle-id={vehicle.id}
        data-vehicle-type-id={vehicle.vehicle_type_id}
        onClick={() => {
          switch (type) {
            case 'none':
              this.props.dispatch(
                push(
                  `/bookings/create?${queryString.stringify({
                    start_date: day.date.format('YYYY-MM-DD'),
                    end_date: day.date.format('YYYY-MM-DD'),
                    vehicle_type_id: vehicle.vehicle_type_id,
                  })}`,
                ),
              );
              break;
            case 'hire':
              this.props.dispatch(
                push(`/bookings/${event.id}?from=availabilities`),
              );
              break;
            case 'reservation':
              this.props.dispatch(
                push(`/reservations/${event.id}?from=availabilities`),
              );
              break;
          }
        }}
      >
        {type === 'hire' && (
          <span>
            {event.contact_name ? initials(event.contact_name) : 'N/A'}
          </span>
        )}
        {type === 'reservation' && <Icon icon={reservationIcon(event)} />}
        {type === 'none' && <Icon icon="plus" size="large" />}
      </div>
    );

    return <DraggableItem key={row}>{result}</DraggableItem>;
  }

  /**
   * Render the week block
   */
  render() {
    const days = this.prepareDays();
    if (this.state.isMovingEvent) {
      return <Loader fullwidth />;
    }
    return (
      <div className="weekblock">
        <div className="weekblock--container">
          {this.renderDates(days)}
          {this.props.vehicles !== undefined && (
            <div className="columns is-gapless is-marginless is-mobile">
              <div className="column">
                <div className="weekblock--vehicles">
                  {sortBy(this.props.vehicles, [
                    'vehicle_type_size',
                    'vehicle_type_id',
                  ])
                    .filter(this.props.vehicleTypeFilter)
                    .map((vehicle) => this.renderVehicle(vehicle))}
                </div>
              </div>
              {days.map((day, index) => this.renderDay(day, index))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default connect()(WeekBlock);
